import React, { useEffect, useMemo, useState } from 'react';
import { Modal, InputNumber, Button, Alert, Tooltip } from 'antd';
import { pairTradingApi } from '../../../services/pairTradingApi';
import { futureExpiry, formatExpiry, nextContract, isExpiredContract } from '../../../utils/futuresExpiry';
import { formatDecimal } from '../../../utils/numberFormat';

/**
 * Move the pair's futures leg onto another contract.
 *
 * Exists because the engine's auto-roll only fires while the bot is RUNNING, on
 * expiry day, at 14:00. A pair stopped through that window keeps a contract that
 * has since died: the next start heals the symbol but not the reference price
 * (the F1→F2 basis it shifts by is unmeasurable once F1 stops quoting), so the
 * pair runs and never opens. Before this there was no way out — nothing in the
 * UI changes the deriv symbol, and a reference entered on its own gets stamped
 * with the contract still in the DB.
 *
 * The reference price is an INPUT, not a suggestion the server computes. The old
 * target belonged to a contract that no longer trades, and silently adopting the
 * new contract's market price would throw away the operator's entry threshold —
 * so it is prefilled from the live price and left for them to correct.
 */
export interface RollDerivLegModalProps {
    open: boolean;
    onClose: () => void;
    onRolled: () => void;
    dataConfigId: string | null;
    /** Contract the leg is on now, bare or exchange-qualified. */
    currentSymbol: string | null;
    /** Live price of the contract being rolled ONTO, index points. */
    targetMarketPrice: number | null;
    /** The target's live price arrives via this symbol — reported for clarity. */
    onTargetSymbolChange?: (symbol: string | null) => void;
    /** The reference currently stored against the old contract, for contrast. */
    currentReferencePrice: number | null;
}

const GREY = '#848E9C';

const RollDerivLegModal: React.FC<RollDerivLegModalProps> = ({
    open,
    onClose,
    onRolled,
    dataConfigId,
    currentSymbol,
    targetMarketPrice,
    onTargetSymbolChange,
    currentReferencePrice,
}) => {
    const suggestedTarget = useMemo(() => nextContract(currentSymbol), [currentSymbol]);
    const [reference, setReference] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Touched-tracking, so the live price seeds the field once and then stops
    // fighting the operator's typing on every tick.
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTouched(false);
        setError(null);
        onTargetSymbolChange?.(suggestedTarget);
    }, [open, suggestedTarget, onTargetSymbolChange]);

    useEffect(() => {
        if (!open || touched) return;
        if (targetMarketPrice != null) setReference(targetMarketPrice);
    }, [open, touched, targetMarketPrice]);

    const expiry = futureExpiry(currentSymbol);
    const oldExpired = isExpiredContract(currentSymbol);

    const submit = async () => {
        if (!dataConfigId || !suggestedTarget || reference == null || reference <= 0) return;
        setSubmitting(true);
        setError(null);
        try {
            await pairTradingApi.rollDerivLeg(dataConfigId, suggestedTarget, reference);
            onRolled();
            onClose();
        } catch (e: any) {
            // The BE's guards (running / not flat / expired target) all come back
            // with a message written for a human — show it verbatim rather than a
            // generic failure, because each one names a different fix.
            setError(e?.response?.data?.message ?? e?.message ?? 'roll failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title="Roll the futures leg"
            footer={null}
            width={460}
            destroyOnClose
        >
            <div style={{ fontSize: 12, color: '#EAECEF', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: GREY }}>From</span>
                    <span>
                        <strong>{currentSymbol ?? '—'}</strong>
                        {oldExpired && (
                            <span style={{ color: '#F6465D', marginLeft: 6 }}>
                                expired{expiry ? ` ${formatExpiry(expiry)}` : ''}
                            </span>
                        )}
                    </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: GREY }}>To</span>
                    <strong>{suggestedTarget ?? '—'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: GREY }}>Market now</span>
                    <span style={{ fontFamily: "'Courier New', monospace" }}>
                        {targetMarketPrice != null ? formatDecimal(targetMarketPrice, 1) : '—'}
                    </span>
                </div>

                <div>
                    <div style={{ color: GREY, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        Target price for {suggestedTarget ?? 'the new contract'}
                        <Tooltip title="The OPEN trigger compares the live price against this. It is not derived from the old target: that one belonged to a contract which no longer trades, and the basis between the two cannot be measured once the old one stops quoting.">
                            <span style={{ cursor: 'help' }}>ⓘ</span>
                        </Tooltip>
                    </div>
                    <InputNumber
                        style={{ width: '100%' }}
                        value={reference}
                        onChange={(v) => { setTouched(true); setReference(v ?? null); }}
                        min={0}
                        step={0.1}
                        placeholder="e.g. 1893.4"
                    />
                    {currentReferencePrice != null && (
                        <div style={{ color: GREY, marginTop: 4, fontSize: 11 }}>
                            Previous target was {currentReferencePrice} on {currentSymbol}.
                        </div>
                    )}
                </div>

                <Alert
                    type="info"
                    showIcon
                    message="The pair must be stopped and the futures leg flat."
                    description={
                        'A running worker resolved its contract at startup and would not ' +
                        'see this change. And rewriting the symbol while legs are still ' +
                        'open strands them on a dead contract, where they count against ' +
                        'max_open and block every future entry.'
                    }
                    style={{ fontSize: 11 }}
                />

                {error && <Alert type="error" showIcon message={error} />}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={submitting}
                        disabled={!dataConfigId || !suggestedTarget || reference == null || reference <= 0}
                        onClick={submit}
                    >
                        Roll
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RollDerivLegModal;
